import React, { useState, useEffect } from 'react';
import AppLayout from './AppLayout';
import Login from './Login';
import Register from './Register';
import Dashboard from './Dashboard';
import PersonnelManagement from './PersonnelManagement';
import AddPersonnel from './AddPersonnel';
import EditPersonnel from './EditPersonnel';
import Reports from './Reports';
import Payroll from './Payroll';
import LeaveRequests from './LeaveRequests';
import UserManagement from './UserManagement';
import EditUser from './EditUser';
import Location from './Location';
import Departments from './Department';
import ArmyType from './ArmyType';
import RankManagement from './RankManagement';
import BioMetricDevices from './BioMetricDevice';
import LeaveManagement from './LeaveManagement';
import AuditLog from './AuditLog'
import IdCardManagement from './IdCardManagement';
import WorkflowManagement from './WorkflowManagement';
import ForgotPassword from './ForgotPassword';
import DailyAttendancePage from './attendance/DailyAttendancePage';
import MyAttendancePage from './attendance/MyAttendancePage';
import AttendanceDetailPage from './attendance/AttendanceDetailPage';
import LiveMonitoringPage from './attendance/LiveMonitoringPage';
import AttendanceSettings from './attendance/AttendanceSettings';
import BiometricSettings from './attendance/BiometricSettings';
import { hasRole } from '../utils/roleHelper';


// API Service
const api = {
  async get(endpoint) {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/v1${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    return response.json();
  },

  async post(endpoint, data) {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/v1${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(data)
    });
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : {};
    } catch (err) {
      parsed = { errors: { base: [`Server error (${response.status}). Please try again.`] } };
    }
    return { response, data: parsed };
  },

  async put(endpoint, data) {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/v1${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(data)
    });
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : {};
    } catch (err) {
      parsed = { errors: { base: [`Server error (${response.status}). Please try again.`] } };
    }
    return { response, data: parsed };
  },

  async patch(endpoint, data) {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/v1${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(data)
    });
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : {};
    } catch (err) {
      parsed = { errors: { base: [`Server error (${response.status}). Please try again.`] } };
    }
    return { response, data: parsed };
  },

  async delete(endpoint) {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/v1${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    // attempt to parse response body if any
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : {};
    } catch (err) {
      parsed = { error: 'Invalid server response' };
    }
    return { response, data: parsed };
  }
};

const App = () => {
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const viewParts = currentView.split('-');
  const subView = viewParts[1];
  const [showForgotPassword, setShowForgotPassword] = useState(false);


  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedView = localStorage.getItem('currentView') || 'dashboard';

    if (token && savedUser) {
      try {
        const userData = await api.get('/users/profile');
        setUser(userData.user);
        const defaultView = hasRole(userData.user, 'personnel_viewer') ? 'personnel-military' : savedView;
        setCurrentView(defaultView);
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleLogin = async (data) => {
    try {
      const userData = await api.get('/users/profile');
      setUser(userData.user);
      localStorage.setItem('user', JSON.stringify(userData.user));
      const targetView = hasRole(userData.user, 'personnel_viewer') ? 'personnel-military' : 'dashboard';
      navigate(targetView);
    } catch (err) {
      console.error('Failed to fetch profile after login');
      setUser(data.user);
      const targetView = hasRole(data.user, 'personnel_viewer') ? 'personnel-military' : 'dashboard';
      navigate(targetView);
    }
  };
  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setShowLogin(true);
  };
  const handleLogout = () => {
    setUser(null);
    setShowLogin(true);
    setCurrentView('dashboard');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentView');
  };

  const navigate = (view, userId = null, personnelId = null) => {
    if (hasRole(user, 'personnel_viewer') && !view.startsWith('personnel')) {
      view = 'personnel-military';
    }
    const fullView = userId && personnelId ? `${view}-${userId}-${personnelId}` : userId ? `${view}-${userId}` : view;
    setCurrentView(fullView);
    localStorage.setItem('currentView', fullView);
  };

  const renderView = () => {
    if (hasRole(user, 'personnel_viewer')) {
      return <PersonnelManagement onBack={() => navigate('personnel-military')} onNavigate={navigate} api={api} subView={subView || 'military'} user={user} />;
    }
    const commonProps = { onBack: () => navigate('dashboard'), onNavigate: navigate };
    const baseView = viewParts[0];
    const id = viewParts[2];
    switch (baseView) {
      case 'personnel':
        return <PersonnelManagement {...commonProps} api={api} subView={subView} user={user} />;
      case 'users':
        return <UserManagement {...commonProps} api={api} />;
      case 'add':
        if (subView === 'personnel') {
          return <AddPersonnel {...commonProps} api={api} />;
        } else if (subView === 'user') {
          return <AddUser {...commonProps} api={api} />;
        }
        break;
      case 'edit':
        if (subView?.startsWith('personnel')) {
          let armySubView = 'military';
          let userId = null;
          let personnelId = null;

          if (['military', 'police', 'discipline'].includes(viewParts[2])) {
            armySubView = viewParts[2];
            userId = viewParts[3];
            personnelId = viewParts[4] || viewParts[3];
          } else {
            userId = viewParts[2];
            personnelId = viewParts[3] || viewParts[2];
          }
          return <EditPersonnel {...commonProps} api={api} userId={userId} personnelId={personnelId} subView={armySubView} />;
        } else if (subView === 'user' && id) {
          return <EditUser {...commonProps} api={api} userId={id} />;
        }
        break;
      case 'reports':
        return <Reports onBack={() => navigate('dashboard')} api={api} user={user} />;
      case 'payroll':
        return <Payroll api={api} user={user} subView={subView} />;
      case 'leave':
        return <LeaveRequests onBack={() => navigate('dashboard')} api={api} user={user} subView={subView} />;
      case 'location':
        return <Location onBack={() => navigate('dashboard')} api={api} />;
      case 'departments':
        return <Departments onBack={() => navigate('dashboard')} api={api} />;
      case 'army_type':
        return <ArmyType onBack={() => navigate('dashboard')} api={api} />;
      case 'ranks':
      case 'rank_management':
        return <RankManagement onBack={() => navigate('dashboard')} api={api} />;
      case 'biometric_devices':
        return <BioMetricDevices onBack={() => navigate('dashboard')} api={api} />;
      case 'audit':
        return <AuditLog onBack={() => navigate('dashboard')} api={api} />;
      case 'id_cards':
        return <IdCardManagement {...commonProps} api={api} />;
      case 'leave_management':
        return <LeaveManagement api={api} />;
      case 'workflow':
        return <WorkflowManagement api={api} />
      case 'attendance':
        if (subView === 'daily') {
          return <DailyAttendancePage {...commonProps} api={api} user={user} />;
        } else if (subView === 'my') {
          return <MyAttendancePage {...commonProps} api={api} user={user} />;
        } else if (subView === 'corrections') {
          return <AttendanceDetailPage {...commonProps} api={api} user={user} />;
        } else if (subView === 'live') {
          return <LiveMonitoringPage {...commonProps} api={api} />;
        } else if (subView === 'settings') {
          return <AttendanceSettings {...commonProps} api={api} />;
        } else if (subView === 'biometric') {
          return <BiometricSettings {...commonProps} api={api} />;
        }
        break;
      default:
        return <Dashboard user={user} onLogout={handleLogout} onNavigate={navigate} api={api} />;
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <AppLayout user={user} onLogout={handleLogout} onNavigate={navigate} currentView={currentView} subView={subView} api={api}>
      {user ? renderView() : (
        showForgotPassword ? (
          <ForgotPassword onBack={handleBackToLogin} />
        ) : showLogin ? (
          <Login
            onLogin={handleLogin}
            onSwitchToRegister={() => { setShowLogin(false); setShowForgotPassword(false); }}
            onSwitchToForgotPassword={() => { setShowForgotPassword(true); setShowLogin(false); }}
            api={api}
          />
        ) : (
          <Register
            onRegister={() => { setShowLogin(true); setShowForgotPassword(false); }}
            onSwitchToLogin={() => { setShowLogin(true); setShowForgotPassword(false); }}
            api={api}
          />
        )
      )}
    </AppLayout>
  );
};

export default App;